import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { RoomEvent, type RemoteParticipant } from 'livekit-client'
import { watchApi } from './api'
import type { ChatMessage, ChatPerson, ReactionKind } from './types'

/** The reactions, in the bar's order (docs/features/watch-together.md). */
export const REACTION_KINDS: readonly ReactionKind[] = ['kudos', 'cheers', 'heart', 'sweat']

/** How long someone shows as typing after the latest pulse that named them. They ping every `TYPING_PING_MS` while typing. */
export const TYPING_HOLD_MS = 4000
/** How often a typing viewer tells the API so. */
export const TYPING_PING_MS = 3000
/** Taps of one reaction within this window go to the API as one request. */
export const REACTION_BATCH_MS = 250
/** The most taps one request carries (the API's limit). */
export const REACTION_BATCH_MAX = 10
/** How long this tab's own taps wait to be found in a pulse; after that they no longer hide anyone else's. */
const OWN_REACTION_MS = 6000

/** One reaction to float: how many of a kind, and whether they are this viewer's own (shown at once, before any pulse). */
export interface ReactionBurst {
  kind: ReactionKind
  count: number
  own: boolean
}

/** Someone typing on the stage, until `until` (a client time). */
export interface Typer {
  person: ChatPerson
  until: number
}

/** A message's likes as shown: the count, and whether this viewer likes it. */
export interface LikeState {
  likes: number
  liked: boolean
}

/** A `stage.pulse` packet (docs/features/watch-together.md, Realtime): what happened on the stage since the last one. */
export interface StagePulsePacket {
  reactions: Partial<Record<ReactionKind, number>>
  typing: ChatPerson[]
  likes: Record<string, number>
}

const isPerson = (value: unknown): value is ChatPerson =>
  typeof value === 'object' && value !== null && typeof (value as Partial<ChatPerson>).id === 'string'

const KINDS: ReadonlySet<string> = new Set(REACTION_KINDS)

/** A pulse from its JSON, keeping only what is well formed; null for anything that isn't a `stage.pulse`. */
export function parsePulse(text: string): StagePulsePacket | null {
  let packet: unknown
  try {
    packet = JSON.parse(text)
  } catch {
    return null
  }
  if (!packet || typeof packet !== 'object' || (packet as { type?: unknown }).type !== 'stage.pulse') return null
  const { reactions, typing, likes } = packet as { reactions?: unknown; typing?: unknown; likes?: unknown }
  const counts: Partial<Record<ReactionKind, number>> = {}
  if (reactions && typeof reactions === 'object') {
    for (const [kind, count] of Object.entries(reactions)) {
      if (KINDS.has(kind) && Number.isInteger(count) && (count as number) > 0) counts[kind as ReactionKind] = count as number
    }
  }
  const likeCounts: Record<string, number> = {}
  if (likes && typeof likes === 'object') {
    for (const [id, count] of Object.entries(likes)) if (Number.isInteger(count) && (count as number) >= 0) likeCounts[id] = count as number
  }
  return { reactions: counts, typing: Array.isArray(typing) ? typing.filter(isPerson) : [], likes: likeCounts }
}

type Listener = () => void

/** Runs a call nobody waits on: a refusal or a throw is dropped (a reaction or a typing ping matters only now). */
function quietly(call: () => Promise<unknown> | unknown) {
  try {
    void Promise.resolve(call()).catch(() => undefined)
  } catch {
    // Dropped, as a refusal is.
  }
}

/** The calls the store makes: the API's, or a story's stand-ins. */
export type PulseApi = Pick<typeof watchApi, 'react' | 'typing' | 'likeChat' | 'unlikeChat'>
const NO_TYPERS: Typer[] = []

/**
 * One stage's pulse on this tab: reactions to float, who is typing, and like counts. It keeps them outside React so a
 * pulse a second re-renders only what shows each (the reaction layer, the typing line, a liked row), never the page.
 * Own reactions float at once and are taken off the next pulses that carry them, so they don't float twice.
 */
export class StagePulseStore {
  readonly stageId: string
  selfId: string | null
  private reactionListeners = new Set<(burst: ReactionBurst) => void>()
  private typingListeners = new Set<Listener>()
  private likeListeners = new Set<Listener>()
  private typers: Typer[] = NO_TYPERS
  private typingTimer: ReturnType<typeof setTimeout> | undefined
  private lastPing = Number.NEGATIVE_INFINITY
  /** Like states changed on this tab or by a pulse, over what the messages say. */
  private likes = new Map<string, LikeState>()
  /** Like counts a pulse brought, for messages this tab hasn't overridden. */
  private pulsedLikes = new Map<string, number>()
  private batches = new Map<ReactionKind, { count: number; timer: ReturnType<typeof setTimeout> }>()
  /** This tab's taps not yet seen in a pulse, with when each was sent. */
  private own = new Map<ReactionKind, number[]>()

  private api: PulseApi

  constructor(stageId: string, selfId: string | null = null, api: PulseApi = watchApi) {
    this.stageId = stageId
    this.selfId = selfId
    this.api = api
  }

  // ── Reactions ──
  onReaction(listener: (burst: ReactionBurst) => void) {
    this.reactionListeners.add(listener)
    return () => void this.reactionListeners.delete(listener)
  }

  private emit(burst: ReactionBurst) {
    this.reactionListeners.forEach((listener) => listener(burst))
  }

  /** This viewer tapped a reaction: it floats now, and goes to the API with any other taps of its kind in the batch window. */
  react(kind: ReactionKind) {
    this.emit({ kind, count: 1, own: true })
    const sent = this.own.get(kind) ?? []
    sent.push(Date.now())
    this.own.set(kind, sent)
    const batch = this.batches.get(kind)
    if (batch) {
      batch.count += 1
      if (batch.count >= REACTION_BATCH_MAX) this.flushReaction(kind)
      return
    }
    this.batches.set(kind, { count: 1, timer: setTimeout(() => this.flushReaction(kind), REACTION_BATCH_MS) })
  }

  private flushReaction(kind: ReactionKind) {
    const batch = this.batches.get(kind)
    if (!batch) return
    clearTimeout(batch.timer)
    this.batches.delete(kind)
    // A reaction that doesn't reach the API has already floated here; nobody else needs to hear it failed.
    quietly(() => this.api.react(this.stageId, kind, batch.count))
  }

  // ── Typing ──
  subscribeTyping = (listener: Listener) => {
    this.typingListeners.add(listener)
    return () => void this.typingListeners.delete(listener)
  }

  getTypers = () => this.typers

  private setTypers(typers: Typer[]) {
    this.typers = typers.length ? typers : NO_TYPERS
    this.typingListeners.forEach((listener) => listener())
    clearTimeout(this.typingTimer)
    if (this.typers.length) {
      const next = Math.min(...this.typers.map((typer) => typer.until)) - Date.now()
      this.typingTimer = setTimeout(() => this.setTypers(this.typers.filter((typer) => typer.until > Date.now())), Math.max(0, next))
    }
  }

  /** This viewer typed in the composer: the API hears it at most every `TYPING_PING_MS`. */
  typing() {
    const now = Date.now()
    if (now - this.lastPing < TYPING_PING_MS) return
    this.lastPing = now
    quietly(() => this.api.typing(this.stageId))
  }

  /** This viewer sent their message: the next keystroke tells the API at once. */
  sent() {
    this.lastPing = Number.NEGATIVE_INFINITY
  }

  /** People whose message just arrived stop showing as typing. */
  spoke(userIds: readonly string[]) {
    if (!this.typers.some((typer) => userIds.includes(typer.person.id))) return
    this.setTypers(this.typers.filter((typer) => !userIds.includes(typer.person.id)))
  }

  // ── Likes ──
  subscribeLikes = (listener: Listener) => {
    this.likeListeners.add(listener)
    return () => void this.likeListeners.delete(listener)
  }

  /** A message's likes as shown now: this tab's change or a pulse's count over the message's own. */
  likeOf(message: ChatMessage): LikeState {
    const own = this.likes.get(message.id)
    if (own) return own
    const pulsed = this.pulsedLikes.get(message.id)
    return { likes: pulsed ?? message.likes ?? 0, liked: Boolean(message.liked) }
  }

  private setLike(id: string, state: LikeState) {
    this.likes.set(id, state)
    this.likeListeners.forEach((listener) => listener())
  }

  /** Likes or unlikes at once; the API's answer settles the count, and a refusal puts it back (the promise rejects). */
  async toggleLike(message: ChatMessage) {
    const before = this.likeOf(message)
    const liked = !before.liked
    this.setLike(message.id, { liked, likes: Math.max(0, before.likes + (liked ? 1 : -1)) })
    try {
      const { data } = liked ? await this.api.likeChat(this.stageId, message.id) : await this.api.unlikeChat(this.stageId, message.id)
      this.setLike(message.id, { likes: data.likes, liked: data.liked })
    } catch (error) {
      this.setLike(message.id, before)
      throw error
    }
  }

  // ── Packets ──
  receive(packet: StagePulsePacket) {
    const now = Date.now()
    for (const kind of REACTION_KINDS) {
      let count = packet.reactions[kind] ?? 0
      if (!count) continue
      // This tab's own taps are in here: they floated already.
      const sent = (this.own.get(kind) ?? []).filter((at) => now - at < OWN_REACTION_MS)
      const mine = Math.min(sent.length, count)
      count -= mine
      this.own.set(kind, sent.slice(mine))
      if (count > 0) this.emit({ kind, count, own: false })
    }

    const typing = packet.typing.filter((person) => person.id !== this.selfId)
    if (typing.length) {
      const until = now + TYPING_HOLD_MS
      const byId = new Map(this.typers.map((typer) => [typer.person.id, typer]))
      typing.forEach((person) => byId.set(person.id, { person, until }))
      this.setTypers([...byId.values()])
    }

    const liked = Object.entries(packet.likes)
    if (liked.length) {
      for (const [id, likes] of liked) {
        const own = this.likes.get(id)
        if (own) this.likes.set(id, { ...own, likes })
        else this.pulsedLikes.set(id, likes)
      }
      this.likeListeners.forEach((listener) => listener())
    }
  }

  dispose() {
    clearTimeout(this.typingTimer)
    // Taps still batching go now rather than being lost with the stage.
    for (const kind of [...this.batches.keys()]) this.flushReaction(kind)
    this.reactionListeners.clear()
    this.typingListeners.clear()
    this.likeListeners.clear()
  }
}

const PulseContext = createContext<StagePulseStore | null>(null)

/**
 * The stage's pulse for everything under it (`StagePulseStore`), fed from the server's packets on `pulse.<stageId>`.
 * A packet that carries a participant is dropped: only the API sends pulses, as only it sends chat. Use inside the
 * room's `RoomContext`.
 */
export function StagePulseProvider({ stageId, selfId, children }: { stageId: string; selfId: string | null; children: ReactNode }) {
  const room = useRoomContext()
  const store = useMemo(() => new StagePulseStore(stageId), [stageId])
  store.selfId = selfId
  useEffect(() => {
    const topic = `pulse.${stageId}`
    const decoder = new TextDecoder()
    const onData = (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, packetTopic?: string) => {
      if (packetTopic !== topic || participant) return
      const packet = parsePulse(decoder.decode(payload))
      if (packet) store.receive(packet)
    }
    room.on(RoomEvent.DataReceived, onData)
    return () => {
      room.off(RoomEvent.DataReceived, onData)
      store.dispose()
    }
  }, [room, stageId, store])
  return <PulseContext.Provider value={store}>{children}</PulseContext.Provider>
}

/** Hands a ready store to everything under it, without a room: for stories and tests. */
export const StagePulseStoreProvider = PulseContext.Provider

/** The stage's pulse, or null outside `StagePulseProvider` (a story, a test): what uses it then shows nothing of it. */
export function useStagePulse() {
  return useContext(PulseContext)
}

const noSubscribe = () => () => undefined
const noTypers = () => NO_TYPERS

/** Who is typing on the stage now, newest pings kept for `TYPING_HOLD_MS`; this viewer never. */
export function useTypers(): Typer[] {
  const store = useStagePulse()
  return useSyncExternalStore(store?.subscribeTyping ?? noSubscribe, store?.getTypers ?? noTypers)
}

/** A message's likes as shown; it follows this tab's toggles and the stage's pulses. */
export function useLike(message: ChatMessage): LikeState {
  const store = useStagePulse()
  // Primitive snapshots: a new object each read would loop useSyncExternalStore.
  const likes = useSyncExternalStore(store?.subscribeLikes ?? noSubscribe, () => (store ? store.likeOf(message).likes : message.likes ?? 0))
  const liked = useSyncExternalStore(store?.subscribeLikes ?? noSubscribe, () => (store ? store.likeOf(message).liked : Boolean(message.liked)))
  return { likes, liked }
}
